import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import SourceForm from '../components/SourceForm';
import SourceLogo from '../components/SourceLogo';
import DismissableInfoCard from '../components/DismissableInfoCard';
import { useAuth } from '../contexts/AuthContext';
import { AUTH_PROVIDERS } from '../lib/authProviders';
import { sourceLabel, sourceTypes } from '../lib/sources';

// Every source type as a tile; choosing one opens its connect form in a modal. The
// open type is `?source=<type>`, so a link (the agent's included) opens it directly.
export default function AddConnectionPage() {
    const { authFetch } = useAuth();
    const [hasConnections, setHasConnections] = useState<boolean | null>(null);
    // Keyed by each provider's configKey; a key is absent until /workspace/integrations answers.
    const [configured, setConfigured] = useState<Record<string, boolean>>({});
    const [searchParams, setSearchParams] = useSearchParams();
    const asked = searchParams.get('source') ?? '';
    const open = sourceTypes.some(t => t.id === asked) ? asked : '';

    useEffect(() => {
        authFetch('/sources')
            .then(res => res.ok ? res.json() : [])
            .then(data => setHasConnections(Array.isArray(data) && data.length > 0))
            .catch(() => setHasConnections(false));
        authFetch('/workspace/integrations')
            .then(res => res.ok ? res.json() : null)
            .catch(() => null)
            .then(data => setConfigured(Object.fromEntries(
                Object.values(AUTH_PROVIDERS).map(p => [p.configKey, data?.[p.configKey] ?? false]),
            )));
    }, [authFetch]);

    const choose = (type: string | null) => setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        if (type) params.set('source', type);
        else params.delete('source');
        return params;
    });

    const unavailable = (id: string): string | null => {
        const provider = AUTH_PROVIDERS[id];
        return provider && configured[provider.configKey] === false
            ? `${provider.label} integration not configured`
            : null;
    };

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <h3 className="text-lg font-semibold text-graphite">
                    {hasConnections ? 'Add a connection' : 'Add your first connection'}
                </h3>
                <p className="text-coolslate text-sm mt-1">Choose a system, document store or website to connect</p>
            </div>
            <DismissableInfoCard
                storageKey="addSource.introDismissed"
                title="What a connection does"
            >
                Connecting a document store lists its <strong>filenames and folder names</strong>.
                After each sync Navaris files the documents worth having and reads them into the
                record, and dismisses matches that are not your documents; the rest stay on the
                connection's Candidates tab for you to file or dismiss. Turn off <strong>File the
                documents Navaris recommends</strong> when connecting to see its recommendations there
                and file them yourself. Connecting a system such as Xero imports the
                relevant <strong>data</strong> directly.
            </DismissableInfoCard>

            <div className="flex flex-wrap gap-3">
                {sourceTypes.map(type => {
                    const reason = unavailable(type.id);
                    return (
                        <button
                            key={type.id}
                            type="button"
                            disabled={!!reason}
                            title={reason ?? undefined}
                            onClick={() => choose(type.id)}
                            className={`w-45 h-45 shrink-0 flex flex-col items-center justify-center gap-3 p-5 bg-white rounded-xl border border-softgrey text-center transition-all ${
                                reason
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:border-brand hover:shadow-md cursor-pointer'
                            }`}
                        >
                            <div className="bg-white w-20 h-20 rounded-xl border border-softgrey p-3 flex items-center justify-center">
                                <SourceLogo type={type.id} size="2xl" alt="" />
                            </div>
                            <span className="text-sm font-medium text-graphite">{type.label}</span>
                            {reason && <span className="text-[11px] text-coolslate -mt-2">Not configured</span>}
                        </button>
                    );
                })}
            </div>

            {open && <ConnectModal type={open} onClose={() => choose(null)} />}
        </div>
    );
}

function ConnectModal({ type, onClose }: { type: string; onClose: () => void }) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const label = sourceLabel(type);
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 bg-graphite/30">
            <div
                className="bg-white rounded-xl border border-softgrey shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                role="dialog"
                aria-modal="true"
                aria-label={`Connect ${label}`}
            >
                <div className="px-5 py-4 border-b border-softgrey flex items-center gap-3 sticky top-0 bg-white z-10">
                    <SourceLogo type={type} size="lg" alt="" />
                    <h4 className="flex-1 navaris-h3">Connect {label}</h4>
                    <button onClick={onClose} aria-label="Close" className="p-1 -m-1 text-coolslate hover:text-graphite">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5">
                    {/* Keyed by type so each type starts from a clean form. */}
                    <SourceForm key={type} sourceType={type} />
                </div>
            </div>
        </div>
    );
}
